import { useEffect, useRef } from 'react';

export const useTabIntersectionObserver = (
  setActiveCard: (card: number) => void,
  numberOfSlides: number,
  anchorRefs: React.MutableRefObject<(HTMLAnchorElement | null)[]>
) => {
  const isTabClickRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (numberOfSlides === 0) return;

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Only update if we're not currently clicking a tab
        if (isTabClickRef.current) return;

        // Find the entry with the highest intersection ratio
        let maxRatio = 0;
        let activeIndex = 0;

        entries.forEach((entry) => {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            activeIndex = anchorRefs.current.indexOf(entry.target as HTMLAnchorElement);
          }
        });

        if (maxRatio > 0.3) { // 30% threshold
          setActiveCard(activeIndex);
        }
      },
      { 
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: '0px'
      }
    );

    // Observe all anchor elements
    anchorRefs.current.forEach((anchor) => {
      if (anchor && observerRef.current) {
        observerRef.current.observe(anchor);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [numberOfSlides, setActiveCard, anchorRefs]);

  return {
    isTabClickRef
  };
};
